import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'components/Button';

const ModalWithChildren = ({
  isOpen,
  isLoading,
  ctaTitle,
  ctaType,
  cta2Title,
  onClickCta,
  onClickCta2,
  header,
  className,
  headerClassName,
  children,
}) => {
  const [_isOpen, setIsOpen] = useState(isOpen);
  const isCollapsed = useSelector((state) => state.sidebar.isCollapsed);

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (_isOpen) {
      if (document.body.style.overflow !== 'hidden') {
        document.body.style.overflow = 'hidden';
      }
    }
    return () => {
      if (document.body.style.overflow !== 'auto') {
        document.body.style.overflow = 'auto';
      }
    };
  }, [_isOpen]);

  const wrapperClassName = useMemo(() => {
    if (isCollapsed) {
      return 'wrapper-collapsed';
    } else {
      return 'wrapper-uncollapsed';
    }
  }, [isCollapsed]);

  const renderTitle = () => (
    <div
      className={`modal-header ${headerClassName} items-start border-b border-gray-200`}
    >
      <h2 className="text-lg font-bold">{header}</h2>
      <span
        onClick={onClickCta2}
        data-testid="close-icon"
        className="mt-1 cursor-pointer text-javelin-gray-light"
      >
        <FontAwesomeIcon icon="times" />
      </span>
    </div>
  );

  const renderFooter = () => {
    return (
      <div className="mt-8 flex w-full flex-row justify-end px-12">
        {!!cta2Title && (
          <Button
            testId="cta2-button"
            type="button"
            className="btn-secondary mr-6 h-11 w-40 text-sm"
            onClick={onClickCta2}
            title={cta2Title}
            isDisabled={isLoading}
          />
        )}
        {!!ctaTitle && (
          <Button
            testId="cta-button"
            type={ctaType}
            className="btn-primary h-11 w-40 px-2 text-sm"
            title={ctaTitle}
            showSpinner={isLoading}
            onClick={onClickCta}
            titleHidden={false}
          />
        )}
      </div>
    );
  };

  return _isOpen ? (
    <div
      className={`fixed ${wrapperClassName} inset-0 top-0 bottom-0 z-40 overflow-y-auto bg-white bg-opacity-70`}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      data-testid="modal"
    >
      <div className="flex min-h-screen items-center justify-center text-left">
        <form
          noValidate
          onSubmit={onClickCta}
          className={`mx-auto flex transform flex-col overflow-hidden rounded-lg pb-12 align-bottom shadow-xl transition-all ${className}`}
        >
          <div className="h-auto w-full md:w-144">
            {renderTitle()}
            {children && <div className="modal-body px-12">{children}</div>}
          </div>
          {renderFooter()}
        </form>
      </div>
    </div>
  ) : null;
};

ModalWithChildren.propTypes = {
  isOpen: PropTypes.bool,
  onClickCta: PropTypes.func,
  onClickCta2: PropTypes.func,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  ctaTitle: PropTypes.string,
  cta2Title: PropTypes.string,
  headerClassName: PropTypes.string,
  children: PropTypes.node,
  header: PropTypes.any,
  ctaType: PropTypes.string,
};
ModalWithChildren.defaultProps = {
  isOpen: false,
  isLoading: false,
  className: 'bg-gray-50',
  headerClassName: 'p-12',
  ctaType: 'submit',
};
export default ModalWithChildren;
